import { config } from '../config';

// The session lives in memory only, never in Web Storage: nothing to outlive
// the page, or come back with a restored tab. A reload, or a new tab, gets a
// token from the refresh cookie instead (see `restoreSession`).
let token: string | null = null;
let expiresAt: number | null = null;
let subject: string | null = null;

/**
 * How long before expiry a token is refreshed. Covers clock skew and the time
 * a request spends in flight, so the token the API sees is still live.
 */
export const REFRESH_SKEW_MS = 60_000;

/**
 * How long a refresh (or a sign-out's cookie clearing) may take, including any
 * wait for another tab's turn. Every request and realtime reconnect awaits the
 * one shared refresh, so a stalled one would freeze the whole chat.
 */
export const REFRESH_TIMEOUT_MS = 10_000;

/** Serialises refresh-cookie use across this origin's tabs. */
const REFRESH_LOCK = 'axchat-refresh';

/**
 * Inactivity after which the token is no longer refreshed. An open tab would
 * otherwise refresh forever on its own (every realtime reconnect asks for a
 * credential), keeping an unattended session signed in. 15 minutes is the
 * baseline's idle limit; the session ends when the token in hand expires,
 * at most one token lifetime later.
 */
export const IDLE_LIMIT_MS = 15 * 60_000;

/** User input that counts as someone being there. Composed, so input inside
 * the chat element's shadow DOM reaches `window` too. */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/** The authenticated chat user, as returned by `GET /v1/users/me`. */
export interface CurrentUser {
  user_id: string;
  display_name?: string;
  email?: string;
}

/** Matches chat-core's AuthProvider so it can be assigned to `<axon-chat>.auth`. */
export interface AuthProvider {
  getAuthHeaders(): Promise<Record<string, string>>;
  onAuthRejected?(): void;
}

/**
 * Thrown when the caller is authenticated but not authorized for chat (403) —
 * a valid Axon identity without a provisioned chat account. Distinct from 401
 * (no/expired session) because re-authenticating cannot fix it.
 */
export class ForbiddenError extends Error {
  constructor(message = 'Your Axon account does not have access to Axon Chat.') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

export function getToken(): string | null {
  return token;
}

/** Whose token this is (the `sub` it was issued for), if known. */
export function getSubject(): string | null {
  return subject;
}

/** When the stored token expires (epoch ms), or null if the issuer didn't say. */
export function getTokenExpiry(): number | null {
  return expiresAt;
}

/**
 * Stores the access token and, when the issuer said how long it lives, when it
 * expires. Without `expiresInSecs` the token is never refreshed proactively.
 */
export function setToken(value: string, expiresInSecs?: number): void {
  token = value;
  expiresAt = expiresInSecs && expiresInSecs > 0 ? Date.now() + expiresInSecs * 1000 : null;
}

/**
 * Records who the stored token belongs to (the `sub` claim from the token
 * exchange). A refresh that comes back for anyone else is refused.
 */
export function setSubject(sub: string | undefined): void {
  subject = sub || null;
}

export function clearToken(): void {
  token = null;
  expiresAt = null;
  subject = null;
  // Orphan any refresh in flight: its result belongs to the session just
  // ended, and storing it would sign the user back in after sign-out.
  sessionGeneration += 1;
  inflightRefresh = null;
  retryRefreshAt = 0;
}

let inflightRefresh: Promise<string | null> | null = null;
let sessionGeneration = 0;
// The generation whose refresh the server refused (a 4xx: no cookie, a bad or
// expired one). It will keep refusing, so until the session changes the stored
// token goes out as is and its 401 sends the user to sign in, instead of every
// request re-asking first.
let refusedGeneration = -1;
// After a failure that may pass (5xx, 408/429, network, timeout), when to try
// again. Every request asks for a credential, so without a pause each one
// would wait out its own failed refresh. identity reports an IdP that rejected
// the refresh token as 502, so this also keeps that from hitting the IdP on
// every request.
let retryRefreshAt = 0;
// True from the start of endSession until its logout settles. No refresh may
// start meanwhile: it could take the lock ahead of logout and spend the cookie.
let ending = false;

/** How long to wait before retrying a refresh that failed but may succeed. */
export const REFRESH_RETRY_MS = 30_000;

/** Starts the retry wait, unless the session that asked has since ended. */
function backOff(generation: number): void {
  if (generation === sessionGeneration) retryRefreshAt = Date.now() + REFRESH_RETRY_MS;
}

function isRefusal(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}
let lastActivity = Date.now();
// The generation that went idle. Coming back doesn't revive it: the gap is
// noticed on the first input after it, even across a sleep, when no timer ran.
let idleGeneration = -1;

/** Records user input. Exported for tests; the app uses `trackActivity`. */
export function noteActivity(now = Date.now()): void {
  // Only a live session can go idle. A gap spent signed out (an error page,
  // the forbidden message) must not mark the next session, which may begin in
  // this same page without a clearToken.
  if (token !== null && now - lastActivity > IDLE_LIMIT_MS) idleGeneration = sessionGeneration;
  lastActivity = now;
}

function isIdle(): boolean {
  return idleGeneration === sessionGeneration || Date.now() - lastActivity > IDLE_LIMIT_MS;
}

/**
 * Starts counting user input as activity, for the idle limit. Returns a
 * function that stops it.
 */
export function trackActivity(target: Window = window): () => void {
  const onActivity = () => noteActivity();
  for (const type of ACTIVITY_EVENTS) {
    target.addEventListener(type, onActivity, { passive: true, capture: true });
  }
  return () => {
    for (const type of ACTIVITY_EVENTS) {
      target.removeEventListener(type, onActivity, { capture: true });
    }
  };
}

/**
 * Runs `fn` holding the cross-tab refresh lock. The access token is per tab
 * but the refresh cookie is per browser: without the lock two tabs could spend
 * the same refresh token at once, which an IdP that detects reuse punishes by
 * revoking it. Without Web Locks (old browsers, jsdom) `fn` just runs.
 */
function withRefreshLock<T>(signal: AbortSignal, fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request(REFRESH_LOCK, { signal }, fn);
}

/**
 * Trades the HttpOnly refresh cookie (set by axchat-identity at the token
 * exchange) for a new access token, and stores it. Resolves null when the
 * refresh is refused — no cookie, or the IdP rejected it — leaving the stored
 * token alone so the next request's 401 sends the user to sign in.
 *
 * Concurrent callers share one request: the element fetches in parallel, and
 * each refresh rotates the refresh token, so a second one would race the first.
 * Other tabs are kept out by the refresh lock.
 *
 * The cookie belongs to whoever signed in last in this browser, which need not
 * be this tab's user. A refresh for a different `sub` ends this tab's session
 * rather than silently switching users under it.
 */
export function refreshToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const generation = sessionGeneration;
  if (refusedGeneration === generation) return Promise.resolve(null);
  if (Date.now() < retryRefreshAt) return Promise.resolve(null);
  if (ending) return Promise.resolve(null);
  inflightRefresh = (async () => {
    try {
      const signal = AbortSignal.timeout(REFRESH_TIMEOUT_MS);
      const res = await withRefreshLock(signal, () =>
        fetch(config.refreshPath, { method: 'POST', signal }),
      );
      if (!res.ok) {
        if (isRefusal(res.status)) refusedGeneration = generation;
        else backOff(generation);
        return null;
      }
      const body = (await res.json()) as {
        access_token?: unknown;
        expires_in?: unknown;
        sub?: unknown;
      };
      if (typeof body.access_token !== 'string' || !body.access_token) return null;
      if (generation !== sessionGeneration) return null;
      const sub = typeof body.sub === 'string' ? body.sub : undefined;
      if (subject && sub && sub !== subject) {
        clearToken();
        return null;
      }
      // A session restored from the cookie adopts its user; later refreshes
      // are checked against it.
      if (!subject) setSubject(sub);
      setToken(
        body.access_token,
        typeof body.expires_in === 'number' ? body.expires_in : undefined,
      );
      retryRefreshAt = 0;
      return body.access_token;
    } catch (err) {
      // Network error, timeout, or a body that isn't JSON. Treated like a 5xx:
      // the caller falls back to the stored token (whose 401 leads to sign-in),
      // and the next attempt waits REFRESH_RETRY_MS.
      console.warn('axchat: token refresh failed', err);
      backOff(generation);
      return null;
    } finally {
      // A cleared session already dropped this request, and a newer refresh
      // may now own the slot.
      if (generation === sessionGeneration) inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

/**
 * The stored token, refreshed first if it is within REFRESH_SKEW_MS of expiry.
 * A failed refresh returns the old token rather than nothing: the request it
 * goes on is then rejected with a 401, which is the one path to sign-in.
 *
 * Two things end the session here instead, and return nothing: no input for
 * IDLE_LIMIT_MS when a refresh comes due, or a refresh for another user.
 */
async function currentToken(): Promise<string | null> {
  const current = token;
  if (!current) return null;
  if (expiresAt && Date.now() >= expiresAt - REFRESH_SKEW_MS) {
    if (isIdle()) {
      clearToken();
      return null;
    }
    const refreshed = await refreshToken();
    return refreshed ?? token;
  }
  return current;
}

/**
 * Whether this page has a session: the one in memory, or else one restored
 * from the refresh cookie. A reload or a new tab starts with nothing in
 * memory, and this is how it picks the session back up without a trip to the
 * IdP. False means sign in.
 */
export async function restoreSession(): Promise<boolean> {
  if (token !== null) return true;
  await refreshToken();
  // What's in memory now, not what the refresh returned: a sign-out, or a
  // refresh for another user, may have cleared it meanwhile.
  return token !== null;
}

/**
 * Ends the session in this browser, not just this tab: drops the stored token
 * and has axchat-identity clear its cookies, the refresh cookie included. Left
 * behind, that cookie would let the next person at this browser refresh their
 * way into the previous user's session. Best effort: an unreachable identity
 * service doesn't keep the user signed in here.
 *
 * The token goes first, synchronously, and no refresh may start in this tab
 * until logout settles (a restore would otherwise race it). A refresh already
 * in flight is waited out, and the logout POST runs under the
 * refresh lock, so it comes after any refresh in any tab that got there first:
 * whatever cookie those left, logout clears it, and a later refresh finds none.
 *
 * Never rejects. A logout that fails leaves the cookie, so it's logged; the
 * in-memory token is gone either way.
 */
export async function endSession(): Promise<void> {
  const pending = inflightRefresh;
  ending = true;
  clearToken();
  try {
    await pending;
    const signal = AbortSignal.timeout(REFRESH_TIMEOUT_MS);
    const res = await withRefreshLock(signal, () =>
      fetch(config.logoutPath, { method: 'POST', signal }),
    );
    if (!res.ok) console.warn('axchat: logout failed: HTTP', res.status);
  } catch (err) {
    console.warn('axchat: logout failed', err);
  } finally {
    ending = false;
  }
}

/**
 * A Bearer header for a live token, or an empty object when signed out. Async
 * because it may refresh first — this is the only place the token is renewed.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const t = await currentToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Resolves the current user from the chat API.
 * Returns null on 401 (unauthenticated — the caller should sign in). Throws
 * ForbiddenError on 403 (authenticated but no chat access). Throws on other
 * non-ok responses.
 */
export async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser | null> {
  const res = await fetch(`${config.apiBase}/users/me`, {
    headers: await authHeaders(),
    signal,
  });
  if (res.status === 401) return null;
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) throw new Error(`GET ${config.apiBase}/users/me failed: HTTP ${res.status}`);
  return (await res.json()) as CurrentUser;
}

/**
 * Builds the AuthProvider handed to `<axon-chat>`. The element has no refresh
 * hook: it calls `getAuthHeaders` before every request, on every realtime
 * (re)connect, and ahead of the open socket's `expires_at`, so that is where
 * the token is kept fresh. The fresh token goes to the server in an `auth`
 * frame on the same socket, so the socket isn't rebuilt. The element asks up to
 * a fifth of the remaining lifetime early, before our REFRESH_SKEW_MS window,
 * and gets the unchanged token back until it opens; it keeps asking, closer
 * each time, until the token changes. A server that gives no `expires_at` instead
 * closes the socket at expiry, and the reconnect picks up the refreshed token
 * here. On a rejected (401) request the element calls `onAuthRejected`; we
 * hand control back to the caller.
 *
 * The element only reports 401s from REST calls, so a session that ends here
 * (idle, or another user's refresh) is reported too, once per ending:
 * otherwise a realtime refresh or reconnect would just fail quietly and the
 * chat would go silent. A credential coming back re-arms it.
 */
export function createAuthProvider(onAuthRejected: () => void): AuthProvider {
  let reported = false;
  return {
    getAuthHeaders: async () => {
      const headers = await authHeaders();
      if (headers.Authorization) {
        reported = false;
      } else if (!reported) {
        reported = true;
        onAuthRejected();
      }
      return headers;
    },
    onAuthRejected,
  };
}

/** The caller's entitlements, as returned by `GET /v1/capabilities`. */
export interface Capabilities {
  /** True when the credential carries the chat-manage scope. */
  is_chat_admin: boolean;
}

/**
 * Resolves what the current credential is entitled to, for the `/admin` gate.
 * Same contract as `fetchCurrentUser`: null on 401 (sign in), ForbiddenError
 * on 403, throws on other non-ok responses. A missing `is_chat_admin` reads as
 * false — the service omits flags it does not grant, and a credential without
 * the chat-manage scope is the common case, not an error.
 */
export async function fetchCapabilities(signal?: AbortSignal): Promise<Capabilities | null> {
  const res = await fetch(`${config.apiBase}/capabilities`, {
    headers: await authHeaders(),
    signal,
  });
  if (res.status === 401) return null;
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) throw new Error(`GET ${config.apiBase}/capabilities failed: HTTP ${res.status}`);
  const body = (await res.json()) as { is_chat_admin?: unknown };
  return { is_chat_admin: body.is_chat_admin === true };
}
